import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllCategories } from '../api/categoryApi';
import { getAllCompanies, CompanySummary } from '../api/productApi';
import { useCategoryContext } from '../context/CategoryContext';
import { resolveImageUrl } from '../utils/image';
import { updatePageSEO } from '../utils/seo';
import './CategoriesPage.css';

export default function CategoriesPage() {
  const navigate = useNavigate();
  const { categories: contextCategories } = useCategoryContext();
  const [categories, setCategories] = useState<any[]>([]);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    updatePageSEO({
      title: 'All Fatakda & Firecrackers Categories | Jay Bhavani Fatakda - Sivakasi Wholesale (જય ભવાની)',
      description: 'Explore all Diwali fatakda and firecracker categories and brands: Sparklers, Rockets, Sky shots, Ground Chakkers, Flower Pots, and sound crackers at factory wholesale rates from Jay Bhavani (Jai Bhavani) Fireworks.',
      canonicalPath: '/category',
      keywords: 'fatakda, firecrackers, fatakda online, jay bhavani fatakda, jaibhavani, jaybhavani, jai bhavani fireworks, sivakasi wholesale fatakda, rockets, sparklers, sky shots'
    });
  }, []);

  useEffect(() => {
    const fetchCategoriesAndCompanies = async () => {
      setLoading(true);
      try {
        if (contextCategories && contextCategories.length > 0) {
          setCategories(contextCategories);
        } else {
          const response = await getAllCategories();
          const data = Array.isArray(response) ? response : response.value;
          setCategories(data || []);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setCategories([]);
      }

      try {
        const compList = await getAllCompanies();
        setCompanies(compList || []);
      } catch {
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoriesAndCompanies();
  }, [contextCategories]);

  const handleCategorySelect = (categoryId: number, categoryName: string) => {
    navigate(`/${categoryName.toLowerCase().replace(/\s+/g, '-')}`, { 
      state: { categoryId } 
    });
  };

  const handleCompanySelect = (companyName: string, companyLogoUrl?: string) => {
    const slug = encodeURIComponent(companyName.toLowerCase().trim().replace(/\s+/g, '-'));
    navigate(`/company/${slug}`, {
      state: { companyName, companyLogoUrl }
    });
  };

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    return categories.filter(c => 
      c.categoryName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.categoryDescription?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categories, searchQuery]);

  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return companies;
    return companies.filter(c =>
      c.companyName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [companies, searchQuery]);

  return (
    <div className="categories-page">
      {/* Hero Banner */}
      <div className="categories-banner">
        <div className="categories-banner-content">
          <button className="categories-back-btn" onClick={() => navigate('/')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            <span>Back to Home</span>
          </button>
          <div className="categories-badge">FESTIVE COLLECTION</div>
          <h1 className="categories-title">All Fireworks <span>Categories & Brands</span></h1>
          <p className="categories-breadcrumbs">
            Home &nbsp;/&nbsp; Shop &nbsp;/&nbsp; <span>All Categories & Companies</span>
          </p>

          {/* Search Category Box */}
          <div className="categories-search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search category or company brand (e.g. Sparklers, Rockets, Standard)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="categories-search-input"
            />
            {searchQuery && (
              <button 
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Categories Grid */}
      <div className="categories-content">
        <div className="categories-header">
          <div>
            <h2 className="categories-section-title">
              {searchQuery ? `Search Results for "${searchQuery}"` : 'Browse by Category'}
            </h2>
            <p className="categories-section-subtitle">
              Showing {filteredCategories.length} firecracker {filteredCategories.length === 1 ? 'category' : 'categories'}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="categories-loading">
            <div className="loading-spinner"></div>
            <p>Loading firework categories...</p>
          </div>
        ) : filteredCategories.length > 0 ? (
          <div className="categories-grid">
            {filteredCategories.map((category: any) => (
              <div
                key={category.categoryID}
                className="category-card"
                onClick={() => handleCategorySelect(category.categoryID, category.categoryName)}
                role="button"
                tabIndex={0}
              >
                <div className="category-image-container">
                  {category.imageUrl ? (
                    <img
                      src={resolveImageUrl(category.imageUrl)}
                      alt={category.categoryName}
                      className="category-image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="category-placeholder">
                      <span className="category-icon">🧨</span>
                    </div>
                  )}
                  <div className="category-overlay">
                    <span className="view-products">View Products &rarr;</span>
                  </div>
                </div>
                {(!category.imageUrl || category.categoryDescription) && (
                  <div className="category-info">
                    {!category.imageUrl && <h3 className="category-name">{category.categoryName}</h3>}
                    {category.categoryDescription && (
                      <p className="category-description">{category.categoryDescription}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="categories-empty">
            <div className="empty-icon">📦</div>
            <h3>No Categories Found</h3>
            <p>{searchQuery ? `No category matched "${searchQuery}". Try a different search term.` : "We're working on adding more categories. Please check back later!"}</p>
            {searchQuery && (
              <button className="clear-filter-btn" onClick={() => setSearchQuery('')}>
                Clear Search Filter
              </button>
            )}
          </div>
        )}

        {/* Company / Brand Section - moved after category details */}
        {filteredCompanies.length > 0 && (
          <div className="companies-section-block" style={{ marginTop: 48, paddingTop: 36, borderTop: '1px solid #e2e8f0' }}>
            <div className="categories-header">
              <div>
                <h2 className="categories-section-title">
                  🏢 Browse by Company / Brand
                </h2>
                <p className="categories-section-subtitle">
                  Select a company to view all its fireworks with category sorting
                </p>
              </div>
            </div>

            <div className="ul-company-grid">
              {filteredCompanies.map((comp) => {
                const logoSrc = comp.companyLogoUrl || comp.sampleImageUrl;
                return (
                  <button
                    key={comp.companyName}
                    type="button"
                    className="ul-company-card"
                    onClick={() => handleCompanySelect(comp.companyName, comp.companyLogoUrl)}
                  >
                    <div className="ul-company-card__icon">
                      {logoSrc ? (
                        <img
                          src={resolveImageUrl(logoSrc)}
                          alt={comp.companyName}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <span>🏢</span>
                      )}
                    </div>
                    <div className="ul-company-card__info">
                      <h3 className="ul-company-card__name">{comp.companyName}</h3>
                      <span className="ul-company-card__meta">
                        {comp.productCount} {comp.productCount === 1 ? 'Product' : 'Products'}
                        {comp.categoryCount ? ` • ${comp.categoryCount} Categories` : ''}
                      </span>
                    </div>
                    <span className="ul-company-card__arrow">&rarr;</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
